// Copia lato browser di backend/src/lib/cfPersona.ts: controllo del CF contro data e luogo di nascita
// e suggerimenti per il luogo. L'elenco (public/comuni.json, ~400 KB) si scarica solo quando serve.
import { validaCF } from './codiceFiscale';

const OMOCODIA = 'LMNPQRSTUV';
const MESI = 'ABCDEHLMPRST';
export const normLuogo = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s*\([A-Z]{2}\)\s*$/, '').replace(/[^A-Z]/g, '');

let elenco = null, promessa = null;
export const caricaLuoghi = () => {
  if (elenco) return Promise.resolve(elenco);
  if (!promessa) promessa = fetch('/comuni.json').then(r => r.json()).then(lista => {
    const perCodice = new Map(), perNome = new Map();
    const voci = lista.map(([codice, prov, nome, attivo]) => ({ codice, prov, nome, attivo: !!attivo, n: normLuogo(nome) }));
    for (const v of voci) {
      perCodice.set(v.codice, [...(perCodice.get(v.codice) || []), v]);
      perNome.set(v.n, [...(perNome.get(v.n) || []), v]);
    }
    elenco = { voci, perCodice, perNome };
    return elenco;
  }).catch(() => { promessa = null; return null; });
  return promessa;
};
export const luoghiPronti = () => elenco;

// "PADERNO D'ADDA" → "Paderno d'Adda"
const PICCOLE = new Set(['di', 'del', 'della', 'delle', 'dei', 'degli', 'dello', 'da', 'de', 'sul', 'sulla', 'sull', 'in', 'al', 'alla', 'e', 'nel', 'nell', 'con', 'sopra', 'sotto', 'd', 'l']);
export const titoloLuogo = (s) => s.toLowerCase().replace(/[a-zà-ü]+/g, (w, i) => (i > 0 && PICCOLE.has(w) ? w : w[0].toUpperCase() + w.slice(1)));
export const etichettaLuogo = (v) => (v.prov === 'EE' ? titoloLuogo(v.nome) : `${titoloLuogo(v.nome)} (${v.prov})`);

// Suggerimenti: prima chi inizia con il testo, i comuni di oggi prima dei soppressi
export const suggerisciLuoghi = (testo, max = 8) => {
  const n = normLuogo(testo);
  if (!elenco || n.length < 2) return [];
  const inizio = [], dentro = [];
  for (const v of elenco.voci) {
    if (v.n.startsWith(n)) inizio.push(v);
    else if (n.length >= 3 && v.n.includes(n)) dentro.push(v);
  }
  const ord = (a, b) => (b.attivo - a.attivo) || (a.n.length - b.n.length) || a.nome.localeCompare(b.nome);
  return [...inizio.sort(ord), ...dentro.sort(ord)].slice(0, max);
};
export const luogoEsiste = (s) => (elenco ? elenco.perNome.has(normLuogo(s)) : true);

const codiceLuogoCF = (cf) => cf[11] + [...cf.slice(12, 15)].map(ch => (OMOCODIA.includes(ch) ? String(OMOCODIA.indexOf(ch)) : ch)).join('');
const dataCF = (cf) => {
  const de = (s) => [...s].map(ch => (OMOCODIA.includes(ch) ? String(OMOCODIA.indexOf(ch)) : ch)).join('');
  const g = +de(cf.slice(9, 11));
  return { g: g > 40 ? g - 40 : g, m: MESI.indexOf(cf[8]) + 1, aa: +de(cf.slice(6, 8)), sesso: g > 40 ? 'F' : 'M' };
};

// { valido, errore, campo: 'cf' | 'luogo', sesso }
export const controllaCFPersona = (grezzo, p = {}) => {
  const es = p.nome && p.cognome ? validaCF(grezzo, p.nome, p.cognome) : validaCF(grezzo);
  if (!es.valido) return { ...es, campo: 'cf' };
  const cf = es.cf;
  const d = dataCF(cf);
  if (p.data_nascita && /^\d{4}-\d{2}-\d{2}$/.test(p.data_nascita)) {
    const [a, m, g] = p.data_nascita.split('-').map(Number);
    if (d.g !== g || d.m !== m || d.aa !== a % 100) {
      return { valido: false, campo: 'cf', errore: `Il codice fiscale non corrisponde alla data di nascita: nel codice risulta il ${String(d.g).padStart(2, '0')}/${String(d.m).padStart(2, '0')}/${String(d.aa).padStart(2, '0')}` };
    }
  }
  if (p.luogo_nascita && normLuogo(p.luogo_nascita) && elenco) {
    const voci = elenco.perNome.get(normLuogo(p.luogo_nascita));
    if (!voci) return { valido: false, campo: 'luogo', errore: 'Non trovo questo luogo: sceglilo dall\'elenco che compare mentre scrivi.' };
    const codice = codiceLuogoCF(cf);
    if (elenco.perCodice.has(codice) && !voci.some(v => v.codice === codice)) {
      const l = elenco.perCodice.get(codice);
      const atteso = l.find(v => v.attivo) || l[0];
      return { valido: false, campo: 'cf', errore: `Il codice fiscale non corrisponde al luogo di nascita: nel codice risulta ${etichettaLuogo(atteso)}` };
    }
  }
  return { valido: true, cf, sesso: d.sesso, parziale: es.parziale };
};

// Straniero senza CF (form, 26/10/2026): stati esteri di oggi per la tendina (Svizzera e Francia in cima,
// i casi reali) e controllo "il luogo scritto è un comune italiano" (chi è nato in Italia ha il CF).
const IN_CIMA = ['SVIZZERA', 'FRANCIA'];
export const statiEsteri = () => {
  if (!elenco) return [];
  const stati = elenco.voci.filter(v => v.prov === 'EE' && v.attivo);
  const cima = IN_CIMA.map(n => stati.find(v => v.nome === n)).filter(Boolean);
  return [...cima, ...stati.filter(v => !IN_CIMA.includes(v.nome)).sort((a, b) => a.nome.localeCompare(b.nome))];
};
export const comuneItaliano = (s) => {
  const voci = elenco ? elenco.perNome.get(normLuogo(s)) || [] : [];
  return voci.some(v => v.prov !== 'EE') && !voci.some(v => v.prov === 'EE');
};
export const statoDiLuogo = (s) => (elenco ? (elenco.perNome.get(normLuogo(s)) || []).find(v => v.prov === 'EE') || null : null);
